import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAllFeatures } from '@/contexts/FeaturesContext';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
  </div>
);

/**
 * FeatureGatedRoute - Gates a route based on the tiered package system.
 * Access is granted when the user's effective tier (or per-company module
 * override, or superadmin) unlocks the given feature id. The single source of
 * truth is `canAccess` from the SubscriptionContext (computed server-side).
 */
export const FeatureGatedRoute = ({ children, featureId }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const { loading, canAccess } = useSubscription();
  const features = useAllFeatures();

  if (!token) {
    // Free-tier modules invite guests to register (free) instead of the paid page.
    const feat = (features || []).find((f) => f.id === featureId);
    const minTier = feat?.min_tier || (feat?.is_premium ? 'starter' : 'free');
    const dest = minTier === 'free' ? '/register' : '/subscription';
    return <Navigate to={dest} state={{ from: location.pathname, requiresSubscription: dest === '/subscription' }} replace />;
  }

  if (loading) {
    return <Spinner />;
  }

  if (canAccess(featureId)) {
    return children;
  }

  return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
};

/**
 * ProtectedRoute - Requires user to be logged in AND to have a Companylife-
 * eligible role. Shop-only customers (role `user`) are redirected to their
 * `/kundenkonto`. This prevents Webshop-Kunden vom Zugriff auf Companylife-
 * Module (Dashboard, Team, Fahrtenbuch etc.).
 */
const COMPANYLIFE_ROLES = new Set([
  'superadmin', 'admin', 'editor',
  'company_admin', 'company_user', 'tax_advisor',
]);

const readCurrentUser = () => {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('admin_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
  const user = readCurrentUser();

  if (!token) {
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }

  const role = user?.role || 'user';
  if (!COMPANYLIFE_ROLES.has(role)) {
    // Shop-Kunde versucht auf Companylife-Modul zuzugreifen → sanft ins
    // Kundenkonto zurückschicken. Wir merken uns die Ziel-URL nicht, weil
    // Shop-Kunden bewusst keinen Zugriff auf Companylife bekommen sollen.
    return <Navigate to="/kundenkonto?blocked=shop_only" replace />;
  }

  return children;
};

/**
 * SubscriptionRoute - Requires active subscription
 * Redirects to subscription page if no active subscription
 */
export const SubscriptionRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const { isPremium, loading: isLoading, tier } = useSubscription();
  
  // Not logged in -> redirect to subscription/pricing
  if (!token) {
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }
  
  // Still loading subscription status - show loading spinner
  // Wait for loading to complete before making redirect decision
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  
  // Premium users or users with premium tier can access
  if (isPremium || tier === 'premium' || tier === 'lifetime') {
    return children;
  }
  
  // Double check - maybe context hasn't updated yet, give it one more render
  // This helps with race conditions
  if (tier === undefined || tier === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  
  // No subscription -> redirect to subscription page
  return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
};

/**
 * FreeTrialRoute - Allows limited free access, then requires subscription
 * Used for features like Focus Timer (2 free uses)
 */
export const FreeTrialRoute = ({ children, maxFreeUses = 2, featureKey }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const { isPremium, loading: isLoading } = useSubscription();
  
  // Not logged in -> redirect to subscription/pricing
  if (!token) {
    return <Navigate to="/subscription" state={{ from: location.pathname, requiresSubscription: true }} replace />;
  }
  
  // Still loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  
  // Has subscription -> allow access
  if (isPremium) {
    return children;
  }
  
  // Check free usage counter
  const usageKey = `free_usage_${featureKey}`;
  const currentUsage = parseInt(localStorage.getItem(usageKey) || '0', 10);
  
  if (currentUsage >= maxFreeUses) {
    // Free uses exhausted -> redirect to subscription
    return <Navigate to="/subscription" state={{ 
      from: location.pathname, 
      requiresSubscription: true,
      message: `Du hast deine ${maxFreeUses} kostenlosen Nutzungen aufgebraucht. Upgrade für unbegrenzten Zugang!`
    }} replace />;
  }
  
  return children;
};

export default ProtectedRoute;
